import styled, { keyframes } from 'styled-components';
import type { Signal, SignalStatus } from '../types';
import { SIGNALS_FONT_FAMILY } from '../theme/constants';

interface SignalDetailsDrawerProps {
  signal: Signal | null;
  onClose: () => void;
}

const cellStatus = (c?: number): SignalStatus | 'neutral' => {
  switch (c) {
    case 1:
      return 'green';
    case 2:
      return 'amber';
    case 3:
      return 'red';
    default:
      return 'neutral';
  }
};

export const SignalDetailsDrawer = ({ signal, onClose }: SignalDetailsDrawerProps) => {
  const details = signal?.details;
  const isOpen = Boolean(signal && details?.rows?.length);
  if (!isOpen) return null;

  return (
    <DrawerLayer $open={isOpen} aria-hidden={!isOpen}>
      <Backdrop onClick={onClose} />
      <Drawer $open={isOpen}>
        {signal && details && (
          <>
            <DrawerHeader>
              <div>
                <Eyebrow>{signal.eyebrow}</Eyebrow>
                <Title>{details.title || signal.title}</Title>
                {details.summary && <Summary>{details.summary}</Summary>}
              </div>
              <CloseButton type="button" aria-label="Close expanded view" onClick={onClose}>
                ×
              </CloseButton>
            </DrawerHeader>

            {Boolean(details.coverage?.length) && (
              <CoverageGrid>
                {details.coverage!.map((metric) => (
                  <CoverageMetric key={metric.label}>
                    <CoverageValue>{metric.value}</CoverageValue>
                    <CoverageLabel>{metric.label}</CoverageLabel>
                  </CoverageMetric>
                ))}
              </CoverageGrid>
            )}

            <TableWrap>
              <DetailsTable>
                <thead>
                  <tr>
                    {details.columns.map((column) => (
                      <th key={column}>{column}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {details.rows.map((row, rowIdx) => (
                    <tr key={rowIdx}>
                      {row.cells.map((cell, cellIdx) => (
                        <td
                          key={`${rowIdx}-${cellIdx}`}
                          data-status={cellIdx === 0 ? cellStatus(row.color) : undefined}
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </DetailsTable>
            </TableWrap>
          </>
        )}
      </Drawer>
    </DrawerLayer>
  );
};

const DrawerLayer = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 1000;
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
`;

const Backdrop = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.42);
  backdrop-filter: blur(2px);
  animation: ${keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
  `} 0.18s ease-out;
`;

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
`;

const sheetIn = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;

const Drawer = styled.aside<{ $open: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: min(560px, 92vw);
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 24px;
  background: ${({ theme }) => theme.signals.card_bg};
  border-left: 1px solid ${({ theme }) => theme.signals.card_border};
  box-shadow: ${({ theme }) => theme.signals.card_shadow};
  transform: translateX(${({ $open }) => ($open ? '0' : '100%')});
  animation: ${slideIn} 0.22s cubic-bezier(0.2, 0.8, 0.2, 1);
  transition: transform 0.22s cubic-bezier(0.2, 0.8, 0.2, 1);
  font-family: ${SIGNALS_FONT_FAMILY};
  color: ${({ theme }) => theme.signals.text_primary};

  @media (max-width: 640px) {
    top: auto;
    right: 0;
    bottom: 0;
    height: min(82vh, 680px);
    width: 100%;
    padding: 18px 16px max(18px, env(safe-area-inset-bottom));
    border-left: 0;
    border-top: 1px solid ${({ theme }) => theme.signals.card_border};
    border-radius: 20px 20px 0 0;
    transform: translateY(${({ $open }) => ($open ? '0' : '100%')});
    animation: ${sheetIn} 0.22s cubic-bezier(0.2, 0.8, 0.2, 1);
  }
`;

const DrawerHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 18px;

  @media (max-width: 640px) {
    gap: 12px;
  }
`;

const Eyebrow = styled.div`
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.signals.eyebrow};
`;

const Title = styled.h2`
  margin: 6px 0 0;
  font-size: 24px;
  font-weight: 900;
  line-height: 1.15;

  @media (max-width: 640px) {
    font-size: 20px;
  }
`;

const Summary = styled.p`
  margin: 8px 0 0;
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.signals.text_secondary};
`;

const CoverageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const CoverageMetric = styled.div`
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.signals.divider};
  border-radius: 12px;
  background: ${({ theme }) => theme.signals.chip_bg};
`;

const CoverageValue = styled.div`
  font-size: 16px;
  font-weight: 900;
  color: ${({ theme }) => theme.signals.text_primary};
`;

const CoverageLabel = styled.div`
  margin-top: 3px;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.7px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.signals.text_muted};
`;

const CloseButton = styled.button`
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.signals.card_border};
  border-radius: 999px;
  background: ${({ theme }) => theme.signals.chip_bg};
  color: ${({ theme }) => theme.signals.text_secondary};
  font-size: 18px;
  font-weight: 900;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.signals.text_primary};
  }
`;

const TableWrap = styled.div`
  min-height: 0;
  overflow: auto;
  border: 1px solid ${({ theme }) => theme.signals.divider};
  border-radius: 12px;
  -webkit-overflow-scrolling: touch;
`;

const DetailsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;

  th,
  td {
    padding: 12px 14px;
    text-align: left;
    border-bottom: 1px solid ${({ theme }) => theme.signals.divider};
    color: ${({ theme }) => theme.signals.text_secondary};
    vertical-align: top;

    @media (max-width: 640px) {
      padding: 10px 12px;
      white-space: nowrap;
    }
  }

  th {
    position: sticky;
    top: 0;
    background: ${({ theme }) => theme.signals.card_bg};
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    color: ${({ theme }) => theme.signals.text_muted};
    z-index: 1;
  }

  td {
    font-weight: 800;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }

  td[data-status='red'] {
    color: ${({ theme }) => theme.signals.red_text};
  }

  td[data-status='amber'] {
    color: ${({ theme }) => theme.signals.amber_text};
  }
`;
